<template>
  <OrganismStepper
    v-if="hasStepper"
    v-model="displayedSteps"
    :current-id="currentStepId"
    variant="simple"
    isClickable
    class="mb-5"
    :data-cy="`${currentStepId}-stepper`"
    @clicked="onStepClicked"
  />

  <slot />

  <div class="mt-auto pt-10 flex gap-4">
    <MoleculeButton
      v-if="hasPreviousStep"
      size="md"
      variant="secondary"
      class="flex-1 justify-center"
      :data-cy="`${currentStepId}-previous-button`"
      :label="previousLabel || t('common.previous')"
      :disabled="isSubmittingData"
      @click="goToPreviousStep"
    />
    <MoleculeButton
      v-if="hasNextStep"
      size="md"
      variant="primary"
      class="flex-1 justify-center"
      :data-cy="`${currentStepId}-submit-button`"
      :label="submitLabel ? submitLabel : t('common.next')"
      :disabled="isLoadingData || isSubmittingData || isSubmitDisabled"
      @click="onSubmitClick"
      :loading="isSubmittingData"
    />
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useStepStore } from '@/stores';

import type { StepType } from '@/types/regenerativ.type';
import type { SubscriptionRouteName } from '@/types/router.type';

const stepStore = useStepStore();

const { t } = useI18n();

const { getStepsDisplayOnStepper, getCurrentStep, hasNextStep, hasPreviousStep } =
  storeToRefs(stepStore);
const { goToPreviousStep, goToStepById } = stepStore;

const currentStepId = computed(() => {
  return getCurrentStep.value?.id || '';
});

const hasStepper = computed(() => {
  if (!getCurrentStep.value) {
    return false;
  }

  return getCurrentStep.value.hasStepper;
});

const displayedSteps = computed(() => {
  return getStepsDisplayOnStepper.value.map(step => {
    return {
      id: step.id,
    };
  });
});

type Props = {
  submitLabel?: string;
  previousLabel?: string;
  isLoadingData?: boolean;
  isSubmittingData?: boolean;
  isSubmitDisabled?: boolean;
};

withDefaults(defineProps<Props>(), {
  isLoadingData: false,
  isSubmittingData: false,
  isSubmitDisabled: false,
});

const emit = defineEmits(['submitClicked']);

const onSubmitClick = () => {
  emit('submitClicked');
};

const onStepClicked = (clickedStep: StepType) => {
  if (clickedStep.state === 'success') {
    goToStepById(clickedStep.id as SubscriptionRouteName);
  }
};
</script>
